// ══════════════════════════════════════════════════════════════════
// Schémas Zod partagés front + back
// ══════════════════════════════════════════════════════════════════

import { z } from 'zod';
import { CITY_STAFF_ROLES, PriorityLevel, ReportStatus } from './enums';

// ── Primitives ────────────────────────────────────────────────────
export const uuidSchema = z.string().uuid();
export const emailSchema = z.string().email().max(320).toLowerCase().trim();
export const passwordSchema = z
  .string()
  .min(12, 'Le mot de passe doit faire au moins 12 caractères')
  .max(200)
  .regex(/[A-Z]/, 'Au moins une majuscule')
  .regex(/[a-z]/, 'Au moins une minuscule')
  .regex(/[0-9]/, 'Au moins un chiffre');

// ── Auth ──────────────────────────────────────────────────────────
export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Mot de passe requis'),
  totpCode: z.string().regex(/^\d{6}$/, 'Code à 6 chiffres').optional(),
  recoveryCode: z.string().trim().min(8).max(20).optional(),
});

export const totpCodeSchema = z.object({ code: z.string().regex(/^\d{6}$/, 'Code à 6 chiffres') });
export const disable2faSchema = z.object({ password: z.string().min(1), code: z.string().regex(/^\d{6}$/) });
export type LoginInput = z.infer<typeof loginSchema>;

export const registerSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  firstName: z.string().min(1).max(100),
  lastName: z.string().min(1).max(100),
  privacyAccepted: z.literal(true, { errorMap: () => ({ message: 'Vous devez accepter la politique de confidentialité' }) }),
  termsAccepted: z.literal(true, { errorMap: () => ({ message: 'Vous devez accepter les conditions d\'utilisation' }) }),
});
export type RegisterInput = z.infer<typeof registerSchema>;

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: passwordSchema,
});
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

export const forgotPasswordSchema = z.object({ email: emailSchema });
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

export const tokenSchema = z.object({ token: z.string().min(20).max(200) });
export type TokenInput = z.infer<typeof tokenSchema>;

export const resetPasswordSchema = tokenSchema.extend({ newPassword: passwordSchema });
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

// ── Coordonnées géographiques ─────────────────────────────────────
export const geoPointSchema = z
  .object({
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
  })
  // 0, 0 (golfe de Guinée) : position absente remplacée par zéro côté client, jamais un vrai lieu
  .refine((p) => !(p.latitude === 0 && p.longitude === 0), {
    message: 'Position manquante : indiquez le lieu du problème',
  });
export type GeoPoint = z.infer<typeof geoPointSchema>;

// ── Signalements ──────────────────────────────────────────────────
/** Canaux de saisie par le personnel municipal (signalement reçu hors application). */
export const STAFF_SOURCE_CHANNELS = ['phone', 'email', 'in_person', 'web'] as const;
/** Canaux de saisie par un citoyen connecté. */
export const CITIZEN_SOURCE_CHANNELS = ['mobile', 'web'] as const;
export const REPORT_SOURCE_CHANNELS = ['mobile', 'web', 'phone', 'email', 'in_person'] as const;
export type ReportSourceChannel = (typeof REPORT_SOURCE_CHANNELS)[number];

export const SOURCE_CHANNEL_LABELS_FR: Record<ReportSourceChannel, string> = {
  mobile: 'Application mobile',
  web: 'Site web',
  phone: 'Téléphone',
  email: 'Courriel',
  in_person: 'Au comptoir',
};

/** Clé privée renvoyée par POST /uploads/presign */
export const photoKeySchema = z.string().min(10).max(300).regex(/^uploads\/[0-9a-f-]{36}\/[0-9a-f-]{36}\.(jpg|png|webp)$/, 'Photo invalide');

export const createReportSchema = z.object({
  // Personnel : ville de saisie. Citoyen : facultatif, la ville est déduite du lieu
  // (un signalement dans une municipalité non cliente reste possible).
  tenantId: uuidSchema.optional(),
  // Une catégorie, un ou plusieurs types de problème de cette catégorie
  categoryId: uuidSchema,
  subcategoryIds: z.array(uuidSchema).min(1, 'Choisissez au moins un type de problème').max(5),
  // Titre facultatif : à défaut, les types choisis
  title: z.string().trim().min(3, 'Titre trop court').max(300).optional(),
  // Obligatoire (10 caractères) quand un type l'exige, ex. « Préciser le problème »
  description: z.string().trim().max(5000).default(''),
  location: geoPointSchema,
  address: z.string().max(500).optional(),
  addressStreet: z.string().max(300).optional(),
  addressCity: z.string().max(200).optional(),
  addressPostalCode: z.string().max(20).optional(),
  locationAccuracyMeters: z.number().positive().optional(),
  photoKeys: z.array(photoKeySchema).max(5).optional(),
  // Coordonnées de la personne qui signale (saisie par le personnel pour un appel téléphonique)
  citizenName: z.string().max(200).optional(),
  citizenEmail: emailSchema.optional(),
  citizenPhone: z.string().max(50).optional(),
  citizenNotifyEmail: z.boolean().default(true),
  sourceChannel: z.enum(REPORT_SOURCE_CHANNELS).default('web'),
});
export type CreateReportInput = z.infer<typeof createReportSchema>;

export const updateReportSchema = z.object({
  status: z.nativeEnum(ReportStatus).optional(),
  priorityLevel: z.nativeEnum(PriorityLevel).optional(),
  assignedToUserId: uuidSchema.nullable().optional(),
  resolutionNotes: z.string().max(5000).optional(),
  resolutionPhotoKeys: z.array(photoKeySchema).max(5).optional(),
  rejectedReason: z.string().max(1000).optional(),
});
export type UpdateReportInput = z.infer<typeof updateReportSchema>;

// ── Portail citoyen ──────────────────────────────────────────────
export const referenceSchema = z.string().trim().toUpperCase().regex(/^[A-Z0-9]{2,12}-\d{4}-\d{4,8}$/, 'Numéro de référence invalide');

/** Avis du citoyen une fois le verdict rendu */
export const PROBLEM_SOLVED_VALUES = ['YES', 'PARTLY', 'NO'] as const;
export const reportFeedbackSchema = z.object({
  rating: z.number().int().min(1).max(5),
  problemSolved: z.enum(PROBLEM_SOLVED_VALUES).optional(),
  comment: z.string().trim().max(1000).optional(),
});
export type ReportFeedbackInput = z.infer<typeof reportFeedbackSchema>;

/** Suivi sans compte : référence et courriel donnés au moment du signalement */
export const trackLookupSchema = z.object({ reference: referenceSchema, email: emailSchema });
/** Lien reçu par courriel : référence et jeton signé */
export const trackLinkSchema = z.object({ ref: referenceSchema, t: z.string().min(16).max(64) });
export const publicFeedbackSchema = reportFeedbackSchema.extend({ ref: referenceSchema, t: z.string().min(16).max(64) });

/** Appareil qui reçoit les notifications instantanées */
export const pushDeviceSchema = z.object({
  token: z.string().min(20).max(4096),
  platform: z.enum(['android', 'ios', 'web']),
});

const csvOrArray = <T extends z.ZodTypeAny>(item: T) =>
  z.preprocess(
    (v) => (typeof v === 'string' ? v.split(',').filter(Boolean) : v),
    z.array(item),
  ).optional();

/** Filtres communs à la liste, la carte, les statistiques et les exports. */
export const reportFiltersSchema = z.object({
  tenantId: uuidSchema.optional(),
  status: csvOrArray(z.nativeEnum(ReportStatus)),
  priority: csvOrArray(z.nativeEnum(PriorityLevel)),
  categoryId: csvOrArray(uuidSchema),
  /** Types de problème : signalements qui en comportent au moins un */
  subcategoryId: csvOrArray(uuidSchema),
  assignedToUserId: uuidSchema.optional(),
  /** Quartier ou district ; « none » = hors de toute zone */
  zoneId: z.union([uuidSchema, z.literal('none')]).optional(),
  search: z.string().trim().max(200).optional(),
  fromDate: z.coerce.date().optional(),
  toDate: z.coerce.date().optional(),
  slaBreached: z.enum(['true', 'false']).transform((v) => v === 'true').optional(),
  /** En retard : délai dépassé, qu'il soit encore ouvert ou résolu hors délai */
  late: z.enum(['true', 'false']).transform((v) => v === 'true').optional(),
  /** Sans personne assignée */
  unassigned: z.enum(['true', 'false']).transform((v) => v === 'true').optional(),
  sourceChannel: csvOrArray(z.string().max(30)),
  bbox: z
    .string()
    .regex(/^-?\d+(\.\d+)?(,-?\d+(\.\d+)?){3}$/, 'bbox = ouest,sud,est,nord')
    .transform((v) => {
      const [west, south, east, north] = v.split(',').map(Number) as [number, number, number, number];
      return { west, south, east, north };
    })
    .optional(),
});
export type ReportFilters = z.infer<typeof reportFiltersSchema>;

export const REPORT_SORT_KEYS = ['createdAt', 'priority', 'status', 'reference', 'title', 'category', 'slaTargetAt'] as const;
export type ReportSortKey = (typeof REPORT_SORT_KEYS)[number];

export const reportListQuerySchema = reportFiltersSchema.extend({
  sort: z.enum(REPORT_SORT_KEYS).default('createdAt'),
  order: z.enum(['asc', 'desc']).default('desc'),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: uuidSchema.optional(),
});
export type ReportListQuery = z.infer<typeof reportListQuerySchema>;

export const reportExportQuerySchema = reportFiltersSchema.extend({
  format: z.enum(['csv', 'geojson', 'json']).default('csv'),
});
export type ReportExportQuery = z.infer<typeof reportExportQuerySchema>;

// ── Tenants ───────────────────────────────────────────────────────
export const createTenantSchema = z.object({
  slug: z
    .string()
    .min(2)
    .max(80)
    .regex(/^[a-z0-9-]+$/, 'Uniquement minuscules, chiffres et tirets'),
  name: z.string().min(2).max(200),
  city: z.string().min(2).max(200),
  countryCode: z.string().length(2).default('CA'),
  regionCode: z.string().max(10).optional(),
  populationEstimate: z.number().int().positive().optional(),
  contactEmail: emailSchema.optional(),
  primaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
  secondaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
  centerLocation: geoPointSchema.optional(),
});
export type CreateTenantInput = z.infer<typeof createTenantSchema>;

// ── Provisioning admin de ville (super admin) ─────────────────────
export const provisionTenantAdminSchema = z.object({
  email: emailSchema,
  firstName: z.string().min(1).max(100),
  lastName: z.string().min(1).max(100),
  phone: z.string().max(50).optional(),
});
export type ProvisionTenantAdminInput = z.infer<typeof provisionTenantAdminSchema>;

// ── Invitations ───────────────────────────────────────────────────
export const inviteUserSchema = z.object({
  email: emailSchema,
  role: z.enum(CITY_STAFF_ROLES),
});
